import { currency } from '@/shared/format'
import { buildFormulaValues, FORMULA_SPHERE_NAME, FORMULA_CYLINDER_NAME, FORMULA_AXIS_NAME, FORMULA_ADDICTION_NAME } from '@/shared/formula'
import { FREE_SHIPPING_THRESHOLD } from '@/shared/delivery'
import { VARIANT_TYPE_INPUT_NAME, VARIANT_TYPE_QUANTITY_FORM } from '@/shared/variants'
import productNotFound from '@/assets/product-not-found.png'

export function getQuantities (state) {
  var quantities = []

  for (let quantity = 1; quantity <= state._product.Cantidad; quantity++) {
    const value = quantity === 1 ? quantity + ' Unidad' : quantity + ' Unidades'
    quantities.push({
      quantity: quantity,
      value: value
    })
  }

  return quantities
}

export function getHumanizedPrice (state) {
  return function (index) {
    const product = state.products.products[index]
    if (product.price) {
      return currency(product.price)
    }

    if (!product.variants) {
      return ''
    }

    const minPrice = Math.min.apply(Math, product.variants.map(function (o) { return o.price }))
    const maxPrice = Math.max.apply(Math, product.variants.map(function (o) { return o.price }))

    if (minPrice === maxPrice) {
      return currency(maxPrice)
    }

    return currency(minPrice) + ' ~ ' + currency(maxPrice)
  }
}

export function getCoverImage (state) {
  return function (index) {
    const product = state.products.products[index]
    if (hasImages(product)) {
      return product.image_urls[0]
    }

    if (!product.variants) {
      return productNotFound
    }

    for (const variant of product.variants) {
      if (hasImages(variant)) {
        return variant.image_urls[0]
      }
    }

    return productNotFound
  }
}

export function getProductImages (state) {
  const product = state._product
  let images = hasImages(product) ? product.image_urls : []

  if (!product.variants || product.variants.length === 0) {
    return images
  }

  for (const variant of product.variants) {
    if (hasImages(variant)) {
      images = images.concat(variant.image_urls)
    }
  }

  return images
}

function hasImages (product) {
  const images = product.image_urls

  return !!images && images.length > 0
}

export function getSelectedImageIndex (state) {
  const images = getProductImages(state)

  const variant = state.selectedVariant
  if (!variant || !variant.image_urls || variant.image_urls.length === 0 || images.length === 0) {
    return 0
  }

  const firstVariantImage = variant.image_urls[0]
  const index = images.indexOf(firstVariantImage)

  return index !== -1 ? index : 0
}

export function isFreeShipping (state) {
  return function (index) {
    const product = state.products.products[index]
    if (product.price) {
      return product.price >= FREE_SHIPPING_THRESHOLD
    }

    const minPrice = Math.min.apply(Math, product.variants.map(function (o) { return o.price }))

    return minPrice >= FREE_SHIPPING_THRESHOLD
  }
}

export function getProductPrice (state) {
  const product = state._product
  if (product.price) {
    return currency(product.price)
  }

  const variant = state.selectedVariant
  if (!!product.variation_theme && !!variant) {
    return currency(variant.price)
  }

  if (!product.variants) {
    return ''
  }

  const minPrice = Math.min.apply(Math, product.variants.map(function (o) { return o.price }))
  const maxPrice = Math.max.apply(Math, product.variants.map(function (o) { return o.price }))

  if (minPrice === maxPrice) {
    return currency(maxPrice)
  }

  return currency(minPrice) + ' ~ ' + currency(maxPrice)
}

export function getProductMinPrice (state) {
  const product = state._product
  if (product.price) {
    return product.price
  }

  const minPrice = Math.min.apply(Math, product.variants.map(function (o) { return o.price }))

  return minPrice
}

export function getProductVariantConfig (state) {
  const product = state._product
  const name = VARIANT_TYPE_INPUT_NAME[product.variation_theme] ? VARIANT_TYPE_INPUT_NAME[product.variation_theme] : product.variation_theme

  const quantityInfo = VARIANT_TYPE_QUANTITY_FORM[product.variation_theme]
  if (!quantityInfo) {
    return { name }
  }

  const variantsQuantity = product.variants.length

  const hint = variantsQuantity === 1 ? variantsQuantity + ' ' + quantityInfo.singular : variantsQuantity + ' ' + quantityInfo.plural

  return {
    name,
    hint,
    hint_disabled_quantity: quantityInfo.disabled_quantity
  }
}

export function getTotalQuantities (state) {
  const product = state._product
  if (product.variation_theme) {
    return product.variants.reduce((sum, variant) => sum + variant.quantity_in_stock, 0)
  }

  return product.quantity_in_stock
}

export function getProductQuantities (state) {
  const product = state._product

  if (product.variation_theme) {
    const variant = state.selectedVariant

    return variant ? buildQuantitiesList(variant.quantity_in_stock) : buildQuantitiesList(0)
  }

  return buildQuantitiesList(product.quantity_in_stock)
}

function buildQuantitiesList (totalQuantity) {
  const quantities = []

  for (let quantity = 1; quantity <= totalQuantity; quantity++) {
    const value = quantity === 1 ? quantity + ' Unidad' : quantity + ' Unidades'
    quantities.push({ quantity, value })
  }

  return quantities
}

export function getSphereValues (state) {
  return buildFormulaRangeValues(state, FORMULA_SPHERE_NAME, 0.25, 2, true)
}

export function getCylinderValues (state) {
  return buildFormulaRangeValues(state, FORMULA_CYLINDER_NAME, 0.25, 2, true)
}

export function getAxisValues (state) {
  return buildFormulaRangeValues(state, FORMULA_AXIS_NAME, 10, 0, false)
}

export function getAddictionValues (state) {
  return buildFormulaRangeValues(state, FORMULA_ADDICTION_NAME, 0.25, 2, true)
}

function buildFormulaRangeValues (state, formulaItem, interval, decimals, handlesPrefix) {
  const product = state._product
  if (!product.available_formulas || product.available_formulas.length === 0) {
    return []
  }

  const ranges = product.available_formulas.find(formula => formula.name === formulaItem)
  if (!ranges) {
    return []
  }

  let items = []

  for (const value of ranges.values) {
    if (!value.max & value.max !== 0) {
      const formulaValues = buildFormulaValues(parseFloat(value.min), parseFloat(value.min), interval, decimals, handlesPrefix)
      items = items.concat(formulaValues)

      continue
    }

    const formulaValues = buildFormulaValues(parseFloat(value.min), parseFloat(value.max), interval, decimals, handlesPrefix)
    items = items.concat(formulaValues)
  }

  return deduplicate(items)
}

function deduplicate (items) {
  const uniqueIds = []

  const unique = items.filter(element => {
    const isDuplicate = uniqueIds.includes(element.key)
    if (!isDuplicate) {
      uniqueIds.push(element.key)

      return true
    }

    return false
  })

  return unique.sort((a, b) => (a.key > b.key) ? 1 : -1)
}

export function getProductsByCategory (state) {
  const productsByCategory = []

  for (const { category, products } of state.homeProducts) {
    const productByCategory = {
      category_id: category.category_id,
      name: getProductNameByCategory(category),
      products: []
    }

    for (const product of products) {
      const newProduct = {
        product_id: product.product_id,
        name: product.name,
        image: getProductImageByCategory(product),
        slug: product.slug,
        brand: product.brand,
        price: getProductPriceByCategory(product)
      }

      productByCategory.products.push(newProduct)
    }

    productsByCategory.push(productByCategory)
  }

  return productsByCategory
}

function getProductNameByCategory (category) {
  if (category.name.toLowerCase() !== 'otros') {
    return category.name
  }

  const previousNameIndex = category.path_from_root.length - 2
  if (previousNameIndex < 0) {
    return category.name
  }

  return category.path_from_root[previousNameIndex].name
}

export function getProductPriceByCategory (product) {
  if (product.price) {
    return currency(product.price)
  }

  if (!product.variants) {
    return ''
  }

  const minPrice = Math.min.apply(Math, product.variants.map(function (o) { return o.price }))
  const maxPrice = Math.max.apply(Math, product.variants.map(function (o) { return o.price }))

  if (minPrice === maxPrice) {
    return currency(maxPrice)
  }

  return currency(minPrice) + ' ~ ' + currency(maxPrice)
}

export function getProductImageByCategory (product) {
  if (hasImages(product)) {
    return product.image_urls[0]
  }

  if (!product.variants) {
    return productNotFound
  }

  for (const variant of product.variants) {
    if (hasImages(variant)) {
      return variant.image_urls[0]
    }
  }

  return productNotFound
}
